import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import MerchantLayout from '@/components/layouts/MerchantLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Key, Trash2, Copy, Loader2, Eye, EyeOff } from 'lucide-react';

const MerchantAPIKeys = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [form, setForm] = useState({ name: '' });

  useEffect(() => {
    fetchAPIKeys();
  }, []);

  const fetchAPIKeys = async () => {
    try {
      const response = await axios.get(`${API}/api-keys`);
      setApiKeys(response.data);
    } catch (error) {
      toast.error('Failed to fetch API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await axios.post(`${API}/api-keys`, form);
      setNewKey(response.data);
      toast.success('API key created! Copy it now - it will not be shown again.');
      setForm({ name: '' });
      fetchAPIKeys();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (keyId) => {
    if (!confirm('Are you sure you want to delete this API key? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api-keys/${keyId}`);
      toast.success('API key deleted');
      fetchAPIKeys();
    } catch (error) {
      toast.error('Failed to delete API key');
    }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('API key copied!');
  };

  return (
    <MerchantLayout title="API Keys">
      <div className="space-y-6" data-testid="api-keys-page">
        {/* Header */}
        <div className="flex items-center justify-between pb-6">
          <p className="text-muted-foreground">Manage API keys for integration</p>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setNewKey(null); }}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-white" data-testid="create-api-key-btn">
                <Plus className="h-4 w-4 mr-2" /> Generate API Key
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="">
                  {newKey ? 'API Key Created' : 'Generate API Key'}
                </DialogTitle>
              </DialogHeader>
              {newKey ? (
                <div className="space-y-4">
                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                    <p className="text-emerald-500 text-sm font-medium mb-2">
                      Save this key now! It will not be shown again.
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-2 bg-muted rounded text-sm font-mono break-all" data-testid="new-api-key">
                        {newKey.key}
                      </code>
                      <Button size="icon" variant="ghost" onClick={() => copyKey(newKey.key)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <Button 
                    className="w-full" 
                    onClick={() => { setDialogOpen(false); setNewKey(null); }}
                  >
                    Done
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleCreate} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Key Name</Label>
                    <Input
                      id="name"
                      placeholder="Production API Key"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                      data-testid="api-key-name-input"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-primary text-white" disabled={creating} data-testid="api-key-submit-btn">
                    {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Generate Key'}
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>

        {/* API Keys List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : apiKeys.length === 0 ? (
          <Card className="border-border bg-card">
            <CardContent className="py-12 text-center">
              <Key className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-base font-semibold font-medium mb-2">No API Keys</h3>
              <p className="text-muted-foreground">Generate your first API key to integrate CoinPayUp</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="text-base font-semibold">Your API Keys</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {apiKeys.map((key) => (
                  <div 
                    key={key.id} 
                    className="flex items-center justify-between px-6 py-4 hover:bg-muted/50 transition-colors"
                    data-testid={`api-key-row-${key.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <Key className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{key.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Created: {new Date(key.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Button 
                      size="icon" 
                      variant="ghost"
                      className="h-8 w-8 text-destructive/60 hover:text-destructive"
                      onClick={() => handleDelete(key.id)}
                      data-testid={`delete-api-key-${key.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Usage Instructions */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-base font-semibold ">API Usage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-muted-foreground text-sm">
                Include your API key in the Authorization header for all API requests:
              </p>
              <pre className="p-4 bg-background border border-gray-200 dark:border-white/10 rounded-lg overflow-x-auto">
                <code className="text-sm font-mono">
{`curl -X POST ${API}/invoices \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"amount": 100, "currency": "USDT", "wallet_id": "..."}'`}
                </code>
              </pre>
            </div>
          </CardContent>
        </Card>
      </div>
    </MerchantLayout>
  );
};

export default MerchantAPIKeys;
